import {
  AlgoliaSearchProductsResponse,
  AlgoliaProductResult,
  AlgoliaPopularSearchResult,
} from '@api-client/src/api/types';

const FACET_MAP: Record<string, string> = {
  collections: 'count.collection_titles',
  vendor: 'count.vendor',
  product_type: 'count.product_type',
  'options.colour': 'count.option_colour',
  'options.size': 'count.option_size',
  variant_options_1: 'count.variant_options_1',
  variant_options_2: 'count.variant_options_2',
  variant_options_3: 'count.variant_options_3',
  inventory_quantity: 'count.inventory_quantity',
  tags: 'count.tags',
};

const getAggregateFilters = (
  facets: Record<string, Record<string, number>>,
  facetMaps: Record<string, string> = {},
) =>
  Object.fromEntries(
    Object.entries({ ...FACET_MAP, ...facetMaps })
      .filter(([facetKey]) => facetKey in facets)
      .map(([facetKey, aggregateKey]) => [
        aggregateKey,
        { count: { counts: facets[facetKey] ?? {} } },
      ]),
  );

const getPopularSearchRecord = (
  result: AlgoliaProductResult | AlgoliaPopularSearchResult,
): AlgoliaPopularSearchResult | null => {
  if (result?.index !== 'shopify_products_query_suggestions') return null;
  return result as AlgoliaPopularSearchResult;
};

export function mapToSearchResponse(
  productSearchResponse: AlgoliaSearchProductsResponse,
  facetMaps: Record<string, string> = {},
) {
  const results = productSearchResponse.results || [];
  if (results.length === 0) return null;

  const baseResult = results[0];

  let mergedFacets: Record<string, Record<string, number>> = {};

  // Use facets from base result only if there's no disjoint facet requests
  const hasMultiQueryResults = results.length > 1;

  if (!hasMultiQueryResults && baseResult.facets) {
    mergedFacets = { ...baseResult.facets };
  }

  // Merge facet results
  results.forEach((result: any) => {
    const facetKeys = Object.keys(result.facets || {});
    facetKeys.forEach((facetKey) => {
      mergedFacets[facetKey] = {
        ...mergedFacets[facetKey],
        ...result.facets[facetKey],
      };
    });
  });

  const popularSearchResult = getPopularSearchRecord(results[1]);
  const popularSearches = popularSearchResult
    ? popularSearchResult.hits
        .slice(0, 6)
        .map(({ query, popularity, _highlightResult, objectID }) => ({
          objectID,
          query,
          popularity,
          highlightResult: _highlightResult?.query?.value,
        }))
    : [];

  const searchResults = {
    activePromotions: [],
    aggregateFilters: getAggregateFilters(mergedFacets, facetMaps),
    results: baseResult.hits.map((hit: any) => {
      const vehicles = hit.meta?.fitment?.vehicles
        ?.map((v: any) => parseInt(v, 10))
        .filter(Number.isInteger);

      return {
        record: {
          ...hit,
          meta: {
            ...hit.meta,
            fitment: {
              ...hit.meta?.fitment,
              vehicles,
            },
          },
        },
      };
    }),
    totalSize: baseResult.nbHits,
    numberOfPages: baseResult.nbPages,
    resultsPerPage: baseResult.hitsPerPage,
    page: baseResult.page,
    variables: {},
    aggregates: {},
    popularSearches,
    index: baseResult.index,
    queryID: baseResult.queryID,
  };

  return searchResults;
}
